import {useEffect, useLayoutEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {setDocumentDescription, setDocumentTitle} from './index';
import {resolveAndSubstituteRouteMeta} from './resolveRouteMeta';
import {DEFAULT_META} from './routeMetaRegistry';

/**
 * Mount once near the top of the routed tree. On every location change it
 * looks up the current pathname in `ROUTE_META_REGISTRY`, substitutes any
 * `{placeholder}` tokens via `resolveVars` / `defaultVars`, and applies the
 * result to `document.title` and `<meta name="description">`.
 *
 * Uses `useLayoutEffect` so page-level overrides (`usePageMeta`, which runs
 * in a regular `useEffect`) always win on the same render.
 */
export const RouteMeta = () => {
  const {pathname} = useLocation();
  useLayoutEffect(() => {
    const resolved = resolveAndSubstituteRouteMeta(pathname);
    if (resolved) {
      // `appendWB=false`: the registry title is the full, authoritative title and
      // must match the SSR host (`host/src/meta.ts`), which emits it verbatim.
      setDocumentTitle(resolved.title, false);
      setDocumentDescription(resolved.description);
      return;
    }
    // No declared meta for this route: fall back to the app-wide default
    // (matches the host's `defaultMetadata` so SSR and CSR agree).
    setDocumentTitle(DEFAULT_META.title, false);
    setDocumentDescription(DEFAULT_META.description);
  }, [pathname]);
  return null;
};

/**
 * Per-page escape hatch for titles/descriptions that depend on runtime state
 * rather than URL params (e.g. a project's display name fetched via GraphQL).
 * Prefer declaring meta in `ROUTE_META_REGISTRY` when the value is derivable
 * from `useParams` — that keeps copy in one place and ships to SSR for free.
 */
export function usePageMeta(override: {title?: string; description?: string}) {
  const {title, description} = override;
  useEffect(() => {
    if (title != null) {
      setDocumentTitle(title);
    }
    if (description != null) {
      setDocumentDescription(description);
    }
  }, [title, description]);
}
